/**
 * Content rewriter that converts markdown note links to NotePlan wiki links
 * Converts [text](../note.md) → [[text]] for internal note references
 */

import { parseMarkdownLinks, type MarkdownLink } from './parseMarkdownLinks'
import { noteTitleFromPath } from './noteTitleFromPath'

/**
 * Rewrites markdown content to convert note links to wiki links
 * @param content - The original markdown content
 * @param notePath - The path of the current note (for relative path resolution)
 * @returns The content with note links converted to wiki links
 */
export function rewriteNoteLinks(content: string, notePath: string): string {
  // Parse all links in the content
  const links = parseMarkdownLinks(content, notePath)
  
  // Filter to only note links (markdown files)
  const noteLinks = links.filter(link => link.kind === 'note')
  
  // If no note links found, return original content
  if (noteLinks.length === 0) {
    return content
  }
  
  // Sort links by position in reverse order to avoid offset issues when replacing
  const sortedLinks = noteLinks.sort((a, b) => {
    const aIndex = content.indexOf(a.raw)
    const bIndex = content.indexOf(b.raw)
    return bIndex - aIndex
  })
  
  let updatedContent = content
  
  // Replace each note link with a wiki link
  for (const link of sortedLinks) {
    const wikiLinkText = getWikiLinkText(link)
    const wikiLink = `[[${wikiLinkText}]]`
    
    // Replace the first occurrence of the raw link
    const linkIndex = updatedContent.indexOf(link.raw)
    if (linkIndex !== -1) {
      updatedContent = 
        updatedContent.slice(0, linkIndex) + 
        wikiLink + 
        updatedContent.slice(linkIndex + link.raw.length)
    }
  }
  
  return updatedContent
}

/**
 * Determines the appropriate text to use inside wiki link brackets
 * @param link - The parsed markdown link
 * @returns The text to use in the wiki link
 */
function getWikiLinkText(link: MarkdownLink): string {
  // If the link has meaningful text (not empty and not just the filename), use it
  if (link.text && link.text.trim() !== '' && link.text !== link.target) {
    return link.text
  }
  
  // Otherwise, extract the note title from the target path
  return noteTitleFromPath(link.resolved)
}

// Re-export the MarkdownLink type for convenience
export type { MarkdownLink } from './parseMarkdownLinks'
